/**
 * SyllabusPickOverlay — 시험지 조립 창의 강의 칸(요청서 판 2 §4-3·§4-4, 판 3 §3).
 *
 * 처음에는 검색 열을 덮는 덮개였다(창에 네 번째 열을 세울 자리가 없어서). 2026-09-14 오너 결정으로 **붙박이 칸**이 됐다 —
 * 창을 열면 펼쳐져 있고 왼쪽 손잡이로 접는다. 줄을 골라도 닫히지 않고, 뒤를 어둡게 덮는 막·초점 가두기·ESC 닫기도 없다.
 * 자리는 부르는 쪽이 낸다: 펼친 동안 지면 미리보기가 오른쪽 서랍으로 물러난다(PaperComposeModal). 이름은 덮개 시절 그대로다.
 * 접어도 내리지 않는다(hidden) — 다시 펴면 고르던 강의·펼친 줄이 그대로다.
 *
 * 강의는 **이름 옆 드롭다운**에서 고른다(오너 2026-09-14 — 목록을 칸에 늘어놓으면 강의가 늘수록 나무를 밀어낸다).
 * 판은 강의 관리 화면 머리와 같은 부품(SyllabusSwitcher: 범위·이름 찾기·쪽 이동)이고 [새 강의]만 끈다.
 * 이름 줄 끝의 [전체 펼치기/접기]는 하위가 있는 행을 한꺼번에 펴고 접는다.
 *
 * 이 칸은 강의·행 탐색만 담당하고, 선택한 조건은 제작 창의 공용 편집기에서 이번 검색에 맞게 조절한다.
 * 새 탭인 이유: 이 창에는 저장 전의 시험지가 들어 있어, 같은 탭에서 옮기면 그것을 잃는다.
 *
 * 조건 있는 줄을 누르면 onPick 으로 그 줄을 넘기고, 부르는 쪽이 본문 검색으로 바꿔 부른다.
 * 조건 없는 제목 줄은 누르면 펼치고 접을 뿐이다 — 찾을 조건이 없다.
 * 나무의 줄마다 건수를 세지 않는다 — 줄 수만큼 왕복이 나고 취소 장치도 없다(판 2 §4-4, 회신 §6).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ExternalLink, AlertTriangle, ChevronDown, ChevronsDownUp, ChevronsUpDown,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../common';
import SyllabusTree from './SyllabusTree';
import SyllabusSwitcher from './SyllabusSwitcher';
import { buildIndex, expandTo, parentOf } from './treeIndex';
import {
  readOpenIds, writeOpenIds, readLastPick, writeLastPick,
} from './openState';
import { listSyllabi, getSyllabus, describeSyllabusError } from './syllabusApi';
import MathName from './MathName';

const ringCls = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';

/** 줄의 자리 — 최상위 줄부터 그 줄까지의 이름(깊이는 다섯 단이 끝이라 여섯 번에서 멈춘다) */
export function pathOf(index, id) {
  const names = [];
  let cur = id == null ? null : String(id);
  for (let i = 0; cur != null && i < 6; i += 1) {
    const it = index.byId.get(cur);
    if (!it) break;
    names.unshift(it.name);
    cur = parentOf(index, cur);
  }
  return names;
}

function SyllabusPickOverlay({
  id = undefined,
  hidden = false,
  onPick,
  activeSyllabusId = null,
  activeNodeId = null,
}) {
  const { getAccessToken } = useAuth();
  // 마지막에 쓴 줄 — 칸이 창과 함께 살아 있으므로 고를 때마다 새로 적는다(강의를 해제하면 머리에 그 줄이 남는다)
  const [last, setLast] = useState(readLastPick);
  // 처음 펼칠 강의를 고르는 데만 쓰는 목록 — 강의 바꾸기는 드롭다운(SyllabusSwitcher)이 제 목록을 따로 부른다
  const [list, setList] = useState({ loading: true, items: [], error: null });
  const [sid, setSid] = useState(() => activeSyllabusId == null ? (last?.syllabusId == null ? null : String(last.syllabusId)) : String(activeSyllabusId));
  const [switchOpen, setSwitchOpen] = useState(false);
  const [detailKey, setDetailKey] = useState(0);
  const [detail, setDetail] = useState({ loading: false, data: null, error: null });
  const [expanded, setExpanded] = useState(new Set());
  const listGen = useRef(0);
  const switchRef = useRef(null);
  // 줄을 고를 때마다 바뀌는 값 — 목록·나무를 다시 받을 까닭이 아니다. 칸이 열린 채 남으므로 의존성에 두면
  // 고를 때마다 나무를 새로 받아 「불러오는 중」이 깜빡이고 스크롤이 맨 위로 간다. 받을 때의 값만 읽는다.
  const pickRef = useRef({ activeSyllabusId, activeNodeId, last });
  pickRef.current = { activeSyllabusId, activeNodeId, last };

  const loadList = useCallback(async () => {
    listGen.current += 1;
    const g = listGen.current;
    setList((s) => ({ ...s, loading: true, error: null }));
    try {
      const d = await listSyllabi({ scope: 'all', q: '', page: 1, perPage: 20 }, getAccessToken);
      if (g !== listGen.current) return;
      setList({ ...d, loading: false, error: null });
      // 처음 펼칠 강의 — 지금 검색을 몰고 있는 강의 > 마지막에 쓴 강의 > 목록 첫 강의
      const has = (x) => x != null && d.items.some((s) => String(s.syllabus_id) === String(x));
      const { activeSyllabusId: active, last: lastPick } = pickRef.current;
      setSid((cur) => {
        if (cur != null) return cur;
        if (has(active)) return String(active);
        if (has(lastPick?.syllabusId)) return String(lastPick.syllabusId);
        return d.items.length ? String(d.items[0].syllabus_id) : null;
      });
    } catch (e) {
      if (g === listGen.current) setList((s) => ({ ...s, loading: false, error: describeSyllabusError(e) }));
    }
  }, [getAccessToken]);

  useEffect(() => { loadList(); return () => { listGen.current += 1; }; }, [loadList]);

  // 고른 강의의 나무 — 펼침은 관리 페이지와 같은 세션 열쇠에, 지금 쓰는 줄·마지막에 쓴 줄의 조상을 더 펼친다
  useEffect(() => {
    if (sid == null) { setDetail({ loading: false, data: null, error: null }); return undefined; }
    let alive = true;
    setDetail({ loading: true, data: null, error: null });
    getSyllabus(sid, getAccessToken)
      .then((d) => {
        if (!alive) return;
        setDetail({ loading: false, data: d, error: null });
        const idx = buildIndex(d.items || []);
        const { activeSyllabusId: active, activeNodeId: node, last: lastPick } = pickRef.current;
        let open = readOpenIds(sid);
        [
          String(active) === sid ? node : null,
          lastPick && String(lastPick.syllabusId) === sid ? lastPick.nodeId : null,
        ].filter((x) => x != null && idx.byId.has(String(x)))
          .forEach((x) => { open = expandTo(idx, String(x), open); });
        setExpanded(open);
      })
      .catch((e) => { if (alive) setDetail({ loading: false, data: null, error: describeSyllabusError(e) }); });
    return () => { alive = false; };
  }, [sid, getAccessToken, detailKey]);

  const data = detail.data && String(detail.data.syllabus_id) === String(sid) ? detail.data : null;
  const index = useMemo(() => buildIndex(data?.items || []), [data]);
  // 하위가 있는 행 — 전체 펼치기가 펼 대상. 최상위 묶음(부모 없음)의 열쇠는 행이 아니라 뺀다
  const expandable = useMemo(() => [...index.children.keys()].filter((k) => index.byId.has(k)), [index]);
  const allOpen = expandable.length > 0 && expandable.every((k) => expanded.has(k));

  const toggle = (nid, open) => {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (open) next.add(String(nid)); else next.delete(String(nid));
      writeOpenIds(sid, next);
      return next;
    });
  };
  const toggleAll = () => {
    const next = allOpen ? new Set() : new Set(expandable);
    writeOpenIds(sid, next);
    setExpanded(next);
  };
  const choose = (it) => {
    const nid = String(it.node_id);
    if (it.query == null) {
      if (index.children.get(nid)?.length) toggle(nid, !expanded.has(nid));
      return;
    }
    const lastPick = { syllabusId: data.syllabus_id, nodeId: it.node_id, name: it.name };
    writeLastPick(lastPick);
    setLast(lastPick);
    onPick({
      syllabusId: data.syllabus_id,
      syllabusName: data.name,
      nodeId: it.node_id,
      name: it.name,
      path: [data.name, ...pathOf(index, nid)],
      query: it.query,
    });
  };
  const lastNode = last && data && String(last.syllabusId) === String(data.syllabus_id)
    ? index.byId.get(String(last.nodeId)) || null : null;

  return (
    <aside
      id={id}
      aria-label="강의 고르기"
      hidden={hidden}
      /* 접힌 동안 display 클래스를 걸지 않는다 — Tailwind 의 flex 가 [hidden] 의 display:none 을 이긴다 */
      className={`${hidden ? '' : 'flex'} min-h-0 w-80 shrink-0 flex-col border-r border-gray-200 bg-white`}
    >
      <div className="shrink-0 space-y-2 border-b bg-gray-50 px-3 py-2.5">
        <h3 className="text-xs font-bold text-gray-900">강의 고르기</h3>
        <div className="flex min-w-0 items-center gap-1">
          {/* 이름이 곧 [강의 바꾸기] — 강의 관리 화면 머리(SyllabusHeader)와 같은 얼굴 */}
          <div className="min-w-0 flex-1">
            <button
              ref={switchRef}
              type="button"
              onClick={() => setSwitchOpen((v) => !v)}
              aria-haspopup="dialog"
              aria-expanded={switchOpen}
              aria-label={`강의 바꾸기 — 지금 ${data?.name || '강의'}`}
              title={`${data?.name || '강의'} — 눌러서 강의 바꾸기`}
              className={`inline-flex max-w-full items-center gap-0.5 rounded-lg px-1 py-0.5 text-left text-xs font-bold text-gray-800 transition-colors hover:bg-gray-100 ${ringCls}`}
            >
              <span className="min-w-0 truncate">{data?.name || '강의'}</span>
              <ChevronDown aria-hidden="true" className="size-3.5 shrink-0 text-gray-400" />
            </button>
          </div>
          {data && expandable.length > 0 && (
            <button
              type="button"
              onClick={toggleAll}
              className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-xs text-gray-600 transition-colors hover:bg-gray-100 ${ringCls}`}
            >
              {allOpen
                ? <ChevronsDownUp aria-hidden="true" className="size-3.5" />
                : <ChevronsUpDown aria-hidden="true" className="size-3.5" />}
              {allOpen ? '전체 접기' : '전체 펼치기'}
            </button>
          )}
        </div>
        {last && activeNodeId == null && (
          <p className="text-[11px] text-gray-500">
            마지막에 쓴 줄 —{' '}
            {lastNode && lastNode.query != null
              ? <button type="button" onClick={() => choose(lastNode)} className="font-medium text-primary underline"><MathName text={last.name} compact /></button>
              : <span className="font-medium text-gray-700"><MathName text={last.name} compact /></span>}
          </p>
        )}
      </div>

      {switchOpen && (
        <SyllabusSwitcher
          anchorRef={switchRef}
          currentId={sid}
          getAccessToken={getAccessToken}
          onSwitch={(next) => { setSwitchOpen(false); setSid(String(next)); }}
          onClose={() => setSwitchOpen(false)}
          canCreate={false}
        />
      )}

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {/* 처음 펼칠 강의를 못 정했을 때만 목록 상태를 말한다 — 강의가 정해진 뒤의 목록 실패는 나무와 무관하다 */}
        {sid == null && list.error && (
          <div role="alert" className="flex items-start gap-2 rounded-2xl bg-red-50 p-3 text-xs text-red-600">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            <span className="min-w-0 flex-1">{list.error}</span>
            <button type="button" onClick={loadList} className="shrink-0 underline hover:text-red-700">다시 시도</button>
          </div>
        )}
        {sid == null && list.loading && <p className="px-2 py-6 text-center text-xs text-gray-400">강의를 불러오는 중…</p>}
        {sid == null && !list.loading && !list.error && (
          <p className="px-2 py-6 text-center text-xs text-gray-400">볼 수 있는 강의가 없습니다.</p>
        )}
        {detail.error && (
          <div role="alert" className="flex items-start gap-2 rounded-2xl bg-red-50 p-3 text-xs text-red-600">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            <span className="min-w-0 flex-1">{detail.error}</span>
            <Button size="xs" variant="secondary" onClick={() => setDetailKey((n) => n + 1)}>다시 시도</Button>
          </div>
        )}
        {detail.loading && <p className="px-2 py-6 text-center text-xs text-gray-400">나무를 불러오는 중…</p>}
        {data && (
          <SyllabusTree
            items={data.items || []}
            mode="pick"
            readOnly
            selectedId={String(activeSyllabusId) === String(data.syllabus_id) ? activeNodeId : null}
            onSelect={choose}
            expanded={expanded}
            onToggle={toggle}
            emptyText="이 강의에는 줄이 없습니다."
          />
        )}
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-t px-3 py-2 text-[11px] text-gray-500">
        <span>행 선택 후 이번 검색 조건을 조절합니다.</span>
        <a
          href={sid != null ? `/syllabi/${sid}` : '/syllabi'}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 font-medium text-primary underline"
        >
          관리 페이지에서 열기 <ExternalLink aria-hidden="true" className="size-3" />
        </a>
      </div>
    </aside>
  );
}

export default SyllabusPickOverlay;
